"use client";

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { format } from "date-fns";
import { getProductHistory } from "@/state/products/product.slice";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight, Loader2, Package, ShoppingCart, ArrowUpDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function ProductHistoryDialog({ productId, productName, open, onOpenChange }) {
    const dispatch = useDispatch();
    const { productHistory, historyLoading } = useSelector((state) => state.product);
    const [activeTab, setActiveTab] = useState("timeline");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);

    // Reset to page 1 when dialog opens or product changes
    useEffect(() => {
        if (open) setPage(1);
    }, [open, productId]);

    // Fetch when open, productId, page, or limit changes
    useEffect(() => {
        if (open && productId) {
            dispatch(getProductHistory({ id: productId, page, limit }));
        }
    }, [open, productId, page, limit, dispatch]);

    const getBadgeVariant = (type, subType) => {
        if (type === "sale") return "destructive";
        switch (subType) {
            case "initial":
                return "secondary";
            case "adjustment":
                return "default";
            case "sale":
                return "destructive";
            case "return":
                return "success";
            case "purchase":
                return "outline";
            default:
                return "outline";
        }
    };

    const formatCurrency = (amount) => `GHS ${parseFloat(amount || 0).toFixed(2)}`;

    const getEventIcon = (type) => {
        switch (type) {
            case "sale":
                return <ShoppingCart className="h-4 w-4" />;
            case "stock":
                return <Package className="h-4 w-4" />;
            default:
                return <ArrowUpDown className="h-4 w-4" />;
        }
    };

    const timeline = productHistory?.timeline || [];
    const pagination = productHistory?.pagination;
    const productInfo = productHistory?.product;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Package className="h-5 w-5" />
                        Product History: {productName || productInfo?.title}
                    </DialogTitle>
                    {productInfo && (
                        <div className="flex gap-4 text-sm text-muted-foreground mt-1">
                            <span>Current Stock: <strong>{productInfo.currentStock}</strong></span>
                            <span>•</span>
                            <span>Stock Events: <strong>{productHistory?.stockHistory || 0}</strong></span>
                            <span>•</span>
                            <span>Sales: <strong>{productHistory?.salesCount || 0}</strong></span>
                        </div>
                    )}
                </DialogHeader>

                <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 overflow-hidden flex flex-col">
                    <TabsList className="grid w-full grid-cols-3">
                        <TabsTrigger value="timeline">Timeline</TabsTrigger>
                        <TabsTrigger value="stock">Stock Changes</TabsTrigger>
                        <TabsTrigger value="sales">Sales</TabsTrigger>
                    </TabsList>

                    <TabsContent value="timeline" className="flex-1 overflow-auto mt-4">
                        <div className="border rounded-lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead className="w-[140px]">Date</TableHead>
                                        <TableHead className="w-[100px]">Type</TableHead>
                                        <TableHead>Details</TableHead>
                                        <TableHead className="w-[100px]">User</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {historyLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={4} className="h-24 text-center">
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                                                Loading history...
                                            </TableCell>
                                        </TableRow>
                                    ) : timeline.length > 0 ? (
                                        timeline.map((event) => (
                                            <TableRow key={event.id}>
                                                <TableCell className="text-sm">
                                                    {format(new Date(event.date), "MMM d, yyyy HH:mm")}
                                                </TableCell>
                                                <TableCell>
                                                    <Badge variant={getBadgeVariant(event.type, event.subType)} className="gap-1">
                                                        {getEventIcon(event.type)}
                                                        {event.type === "sale" ? "SALE" : event.subType?.toUpperCase()}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell>
                                                    {event.type === "stock" ? (
                                                        <div className="flex flex-col">
                                                            <span className={event.change > 0 ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                                                                {event.change > 0 ? "+" : ""}{event.change} units
                                                            </span>
                                                            <span className="text-xs text-muted-foreground">
                                                                {event.previousQuantity} → {event.newQuantity}
                                                                {event.reason && ` • ${event.reason}`}
                                                            </span>
                                                        </div>
                                                    ) : (
                                                        <div className="flex flex-col">
                                                            <span className="font-medium">
                                                                {event.quantity} × {formatCurrency(event.price)} = {formatCurrency(event.total)}
                                                            </span>
                                                            <span className="text-xs text-muted-foreground">
                                                                Order #{event.orderNumber} • {event.customer}
                                                            </span>
                                                        </div>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-sm text-muted-foreground">
                                                    {event.user}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={4} className="h-24 text-center text-muted-foreground">
                                                No history found for this product.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="stock" className="flex-1 overflow-auto mt-4">
                        <div className="border rounded-lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Change</TableHead>
                                        <TableHead>New Qty</TableHead>
                                        <TableHead>Supplier</TableHead>
                                        <TableHead>User</TableHead>
                                        <TableHead>Reason</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {historyLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="h-24 text-center">
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                                                Loading...
                                            </TableCell>
                                        </TableRow>
                                    ) : timeline.filter(e => e.type === "stock").length > 0 ? (
                                        timeline.filter(e => e.type === "stock").map((event) => (
                                            <TableRow key={event.id}>
                                                <TableCell>{format(new Date(event.date), "MMM d, yyyy HH:mm")}</TableCell>
                                                <TableCell>
                                                    <Badge variant={getBadgeVariant("stock", event.subType)}>
                                                        {event.subType?.toUpperCase()}
                                                    </Badge>
                                                </TableCell>
                                                <TableCell className={event.change > 0 ? "text-green-600 font-medium" : "text-red-600 font-medium"}>
                                                    {event.change > 0 ? "+" : ""}{event.change}
                                                </TableCell>
                                                <TableCell>{event.newQuantity}</TableCell>
                                                <TableCell>
                                                    {event.supplier ? (
                                                        <Badge variant="outline">{event.supplier}</Badge>
                                                    ) : "-"}
                                                </TableCell>
                                                <TableCell>{event.user}</TableCell>
                                                <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                                                    {event.reason || "-"}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                                                No stock changes found.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="sales" className="flex-1 overflow-auto mt-4">
                        <div className="border rounded-lg">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Date</TableHead>
                                        <TableHead>Order #</TableHead>
                                        <TableHead>Quantity</TableHead>
                                        <TableHead>Unit Price</TableHead>
                                        <TableHead>Total</TableHead>
                                        <TableHead>Customer</TableHead>
                                        <TableHead>Cashier</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {historyLoading ? (
                                        <TableRow>
                                            <TableCell colSpan={7} className="h-24 text-center">
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin inline" />
                                                Loading...
                                            </TableCell>
                                        </TableRow>
                                    ) : timeline.filter(e => e.type === "sale").length > 0 ? (
                                        timeline.filter(e => e.type === "sale").map((event) => (
                                            <TableRow key={event.id}>
                                                <TableCell>{format(new Date(event.date), "MMM d, yyyy HH:mm")}</TableCell>
                                                <TableCell>
                                                    <Badge variant="outline">#{event.orderNumber}</Badge>
                                                </TableCell>
                                                <TableCell className="font-medium">{event.quantity}</TableCell>
                                                <TableCell>{formatCurrency(event.price)}</TableCell>
                                                <TableCell className="font-medium">{formatCurrency(event.total)}</TableCell>
                                                <TableCell>{event.customer}</TableCell>
                                                <TableCell className="text-muted-foreground">{event.user}</TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={7} className="h-24 text-center text-muted-foreground">
                                                No sales found for this product.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>
                </Tabs>

                {/* Pagination */}
                {pagination && (
                    <div className="flex items-center justify-between pt-4 border-t">
                        <div className="flex items-center space-x-2">
                            <span className="text-sm text-muted-foreground">Rows per page</span>
                            <Select
                                value={limit.toString()}
                                onValueChange={(val) => { setLimit(parseInt(val)); setPage(1); }}
                            >
                                <SelectTrigger className="w-[70px]">
                                    <SelectValue placeholder={limit} />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="10">10</SelectItem>
                                    <SelectItem value="20">20</SelectItem>
                                    <SelectItem value="50">50</SelectItem>
                                    <SelectItem value="100">100</SelectItem>
                                </SelectContent>
                            </Select>
                            {pagination.total > 0 && (
                                <span className="text-sm text-muted-foreground">
                                    {(page - 1) * limit + 1}–{Math.min(page * limit, pagination.total)} of {pagination.total}
                                </span>
                            )}
                        </div>
                        {pagination.pages > 1 && (
                            <div className="flex items-center space-x-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page <= 1 || historyLoading}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    Previous
                                </Button>
                                <div className="text-sm font-medium">
                                    Page {pagination.page} of {pagination.pages}
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                                    disabled={page >= pagination.pages || historyLoading}
                                >
                                    Next
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
